import { useState, useRef, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { billing } from '../lib/api';
import { funnelEvent } from '../lib/funnel';
import { VerifyEmailNotice } from '../components/VerifyEmailNotice';
import './pricing.css';

// ── Plan data — three tiers: Free, Pro (monthly $5.95 / yearly $39) and the
//    one-time Founder's Lifetime ($99). Restored 2026-07-13 after the brief
//    single-$39-one-time experiment. Pro price flips with the billing toggle. ──
type BillingCycle = 'monthly' | 'annual';
type Bullet = { t: string; s?: string };
type Plan = {
  id: 'free' | 'pro' | 'lifetime';
  name: string;
  tagline: string;
  price: string; // headline amount, e.g. '$0' / '$5.95' / '$99'
  unit?: string; // e.g. '/month' / 'one-time'
  note?: string; // small print under the price
  ctaStyle: 'ghost' | 'primary' | 'gold';
  google?: boolean;
  highlight: boolean;
  badge?: string;
  bullets: Bullet[];
  checkout?: 'monthly' | 'annual' | 'lifetime'; // which PayPro product the CTA buys
};

const FREE_PLAN: Plan = {
  id: 'free',
  name: 'Free',
  tagline: 'Start separating today.',
  price: '$0',
  note: 'forever',
  ctaStyle: 'ghost',
  google: true,
  highlight: false,
  bullets: [
    { t: 'Separate any YouTube video into stems' },
    { t: '5 separations per day', s: 'once you sign in' },
    { t: 'All 6 stems', s: 'vocals · drums · bass · guitar · piano · other' },
    // ⚠️ Loop/section caps are stated in FOUR places on this page and one on
    // Billing. Sources of truth: the extension's MAX_LOOP_SECONDS (60) and
    // MAX_LOOP_SECONDS_PRO (420), and the Studio's FREE_SECTION_SECONDS (60).
    // They read 30s/5min here until 2026-09-04 — understating free AND Pro on
    // the page people read before paying. Re-check all five if a cap moves.
    { t: 'Loops up to 1 minute' },
    { t: 'Chords & key for the first 30 seconds', s: 'with guitar shapes' },
    { t: 'Synced lyrics', s: 'free on every plan' },
    { t: 'Runs locally in the Extension', s: 'nothing is uploaded' },
    { t: 'Pitch shift, speed & loop regions' },
  ],
};

// Pro card — price/unit/note depend on the monthly/annual toggle.
function proPlan(cycle: BillingCycle): Plan {
  const monthly = cycle === 'monthly';
  return {
    id: 'pro',
    name: 'Riffloop Pro',
    tagline: 'For serious daily practice.',
    price: monthly ? '$5.95' : '$39',
    unit: monthly ? '/month' : '/year',
    note: monthly ? 'or $39/year — save 45%' : 'Just $3.25/month, billed yearly',
    ctaStyle: 'primary',
    highlight: true,
    badge: 'Most popular',
    checkout: monthly ? 'monthly' : 'annual',
    bullets: [
      { t: 'Everything in Free, plus:' },
      { t: 'Unlimited separations' },
      { t: 'Longer loops — up to 7 minutes', s: 'in the Extension' },
      { t: 'Full-length songs — no 1-minute limit', s: 'in the Studio' },
      { t: 'Chords & key for the whole song', s: 'Free stops at 30 seconds' },
      { t: 'A click locked to the song\u2019s beat', s: 'in the Studio' },
      { t: 'Export your stems', s: 'full length, from your own uploads' },
      { t: 'Better quality separations' },
      { t: 'Unlimited saved loops', s: 'Free keeps 3 per song' },
      { t: 'Complete sync across your devices' },
      { t: 'Early access to new features' },
    ],
  };
}

const LIFETIME_PLAN: Plan = {
  id: 'lifetime',
  name: "Founder's Lifetime",
  tagline: 'Pay once. Yours forever.',
  price: '$99',
  unit: 'one-time',
  note: 'Every future Pro feature included · no subscription',
  ctaStyle: 'gold',
  highlight: false,
  badge: 'Best value',
  checkout: 'lifetime',
  bullets: [
    { t: 'Everything in Pro, forever' },
    { t: 'Pay once — no subscription' },
    { t: 'All future Pro features included' },
    { t: "Locks in today's founder price" },
  ],
};

type Cmp = boolean | string;
const COMPARE: { group: string; rows: { label: string; free: Cmp; pro: Cmp }[] }[] = [
  {
    group: 'Separations',
    rows: [
      { label: 'Separations', free: '5 / day', pro: 'Unlimited' },
      { label: 'Stems per song', free: '6', pro: '6' },
      // Was free:'Studio-grade' / pro:'Better' — which read as if free was the
      // premium tier and Pro a vague step up. Names the two real settings.
      { label: 'Separation quality', free: 'Standard', pro: 'Best' },
    ],
  },
  {
    group: 'Loops & Studio',
    rows: [
      { label: 'Loop length (Extension)', free: 'Up to 1 min', pro: 'Up to 7 min' },
      { label: 'Full-length song separation (Studio)', free: false, pro: true },
      // Chords/click caps: FREE_CHORDS_SECONDS and FREE_METRONOME_SECONDS are
      // both 30 in StudioNext.tsx. ⚠️ The click is NOT gated in the extension.
      { label: 'Chords, key & guitar shapes', free: 'First 30s', pro: 'Whole song' },
      { label: 'Beat-locked click (Studio)', free: 'First 30s', pro: 'Whole song' },
      // Lyrics are free on BOTH surfaces — never sell them as a Pro feature.
      { label: 'Synced lyrics', free: true, pro: true },
      { label: 'Export your stems', free: '60s preview', pro: 'Full length' },
      { label: 'Saved loops per song', free: '3', pro: 'Unlimited' },
      { label: 'Sync across devices', free: false, pro: true },
      { label: 'Pitch shift & speed control', free: true, pro: true },
      { label: 'Loop regions', free: true, pro: true },
    ],
  },
  {
    group: 'Plan',
    rows: [
      { label: 'Browser extension (stems on YouTube)', free: true, pro: true },
      { label: 'Early access to new features', free: false, pro: true },
      { label: 'Billing', free: 'Free forever', pro: 'Monthly, yearly or lifetime' },
    ],
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: 'What counts as a “separation”?',
    a: "Every time you split a song into its 6 stems — vocals, drums, bass, guitar, piano and other — that's one separation. Free gives you 5 a day; Pro is unlimited.",
  },
  {
    q: "What's the difference between Free and Pro quality?",
    a: 'Free already uses studio-grade separation. Pro runs a better model that cleans up bleed between parts even further, and unlocks longer loops, full-length songs, a saved loop library and cross-device sync.',
  },
  {
    q: 'Can I cancel anytime?',
    a: "Yes. Monthly and yearly plans cancel anytime from your account, and you keep Pro until the period you've paid for ends. The Founder's Lifetime is a one-time payment — there's nothing to cancel.",
  },
  {
    q: "What's the Founder's Lifetime deal?",
    a: 'Pay $99 once and keep Pro forever, including every future Pro feature — no subscription, ever. It’s a limited-time launch offer for early supporters, and it locks in today’s price.',
  },
  {
    q: 'Can I download the stems?',
    a: 'On Pro you can export stems from songs you upload yourself in the Studio. Tracks you open from YouTube can be separated and practiced right in the browser, but not downloaded.',
  },
  {
    q: 'Do I need to install anything?',
    a: "The Studio is a web app — nothing to install. To practice right on a YouTube video, add the free Riffloop browser extension. Sign in with Google and you're separating songs in seconds.",
  },
  {
    q: 'What happens to my loops if I go back to Free?',
    a: "We never delete them. Loops beyond the Free limits are simply paused — not removed — and come right back when you're on Pro. Your saved loops are stored locally in your browser (not on our servers), so the one thing to know is that clearing your browser's cache and cookies / site data will erase them.",
  },
];

// ── Icons ──
const Check = ({ cls = '' }: { cls?: string }) => (
  <svg className={'ic ' + cls} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M4.5 10.5 8.5 14.5 15.5 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Dash = () => (
  <svg className="ic dash" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <line x1="6" y1="10" x2="14" y2="10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
const GoogleG = () => (
  <svg className="gg" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
);
const Shield = () => (
  <svg className="ic" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M10 2.5 4 5v4.5c0 3.4 2.4 6.2 6 7.5 3.6-1.3 6-4.1 6-7.5V5l-6-2.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M7.5 10l1.8 1.8L13 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Bolt = () => (
  <svg className="ic" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M11 2 4 11h4l-1 7 7-9h-4l1-7Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const CardIcon = () => (
  <svg className="ic" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="2.5" y="4.5" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M2.5 8h15" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

// ── Animated equalizer (heights fixed once per mount) ──
function Equalizer({ bars = 28, className = '' }: { bars?: number; className?: string }) {
  const heights = useRef(Array.from({ length: bars }, () => 18 + Math.round(Math.random() * 64)));
  return (
    <div className={'eq ' + className} aria-hidden="true">
      {heights.current.map((h, i) => (
        <span
          key={i}
          style={{
            ['--h' as never]: h + '%',
            animationDelay: (i * 0.07).toFixed(2) + 's',
            animationDuration: (1.1 + (i % 5) * 0.18).toFixed(2) + 's',
          }}
        />
      ))}
    </div>
  );
}

function PriceBlock({ plan }: { plan: Plan }) {
  return (
    <div className="price">
      <div className="price-row">
        <span className="amount">{plan.price}</span>
        {plan.unit && <span className="unit">{plan.unit}</span>}
      </div>
      {plan.note && <div className="price-note">{plan.note}</div>}
    </div>
  );
}

function PlanCard({
  plan, ctaLabel, onCta, ctaDisabled,
}: {
  plan: Plan; ctaLabel: string; onCta?: () => void; ctaDisabled?: boolean;
}) {
  return (
    <article className={'card card-' + plan.id + (plan.highlight ? ' is-pro' : '')}>
      {plan.badge && <span className={'badge badge-' + plan.ctaStyle}>{plan.badge}</span>}
      <header className="card-head">
        <h3 className="plan-name">{plan.name}</h3>
        <p className="plan-tag">{plan.tagline}</p>
      </header>
      <PriceBlock plan={plan} />
      <button className={'btn btn-' + plan.ctaStyle} onClick={onCta} disabled={ctaDisabled}>
        {plan.google && !ctaDisabled && <GoogleG />}
        {ctaLabel}
      </button>
      <ul className="feat">
        {plan.bullets.map((bl, i) => (
          <li key={i}>
            <Check cls={plan.id === 'lifetime' ? '' : plan.highlight ? 'pro' : ''} />
            <span>
              {bl.t}
              {bl.s && <em>{bl.s}</em>}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function Compare() {
  const [open, setOpen] = useState(false);
  const cell = (v: Cmp, accent?: boolean) =>
    v === true ? <Check cls={accent ? 'pro' : 'tbl'} /> : v === false ? <Dash /> : <span className="cell-txt">{v}</span>;
  return (
    <section className="compare" id="compare">
      <div className="sec-head">
        <h2>Compare every feature</h2>
        <p>The full breakdown, side by side.</p>
      </div>
      <div className={'tbl-wrap' + (open ? ' open' : '')}>
        <table className="tbl">
          <thead>
            <tr>
              <th className="th-feat"></th>
              <th>Free</th>
              <th className="th-pro">Pro</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE.map((grp) => (
              <Fragment key={grp.group}>
                <tr className="grp-row">
                  <td colSpan={3}>{grp.group}</td>
                </tr>
                {grp.rows.map((r) => (
                  <tr key={r.label}>
                    <td className="td-feat">{r.label}</td>
                    <td>{cell(r.free)}</td>
                    <td className="td-pro">{cell(r.pro, true)}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        <div className="tbl-fade" />
      </div>
      <button className="tbl-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? 'Show less' : 'Show full comparison'}
      </button>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="faq" id="faq">
      <div className="sec-head">
        <h2>Questions, answered</h2>
        <p>Everything else you might be wondering.</p>
      </div>
      <div className="faq-list">
        {FAQ.map((it, i) => (
          <div className={'faq-item' + (open === i ? ' open' : '')} key={i}>
            <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)}>
              <span>{it.q}</span>
              <svg className="chev" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="faq-a"><p>{it.a}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [pending, setPending] = useState<null | 'monthly' | 'annual' | 'lifetime'>(null);
  const [upgradeErr, setUpgradeErr] = useState<string | null>(null);
  // Refused for an unconfirmed address — actionable, so it gets its own panel
  // with a resend button rather than the generic error line.
  const [needsVerify, setNeedsVerify] = useState<{ detail?: string } | null>(null);

  const isPro = !!user && user.plan === 'pro';
  const pro = proPlan(cycle);
  const plans: Plan[] = [FREE_PLAN, pro, LIFETIME_PLAN];

  // The page is the gate for every wall whose card links here, and until
  // 2026-09-05 it reported nothing anywhere — not even GA4. This is a SPA
  // route, so the server's access log never sees a visit either: the 09-05
  // Lifetime buyer went modal → here → checkout and left zero rows. One row
  // per arrival is the denominator for the plan clicks below.
  // Ref, not bare effect: StrictMode runs mount effects twice in dev, and a
  // ref survives that simulated remount, so this stays one row per real visit.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    funnelEvent('rl_pricing_view', { tier: isPro ? 'pro' : 'free' }, 'studio');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startUpgrade(plan: 'monthly' | 'annual' | 'lifetime') {
    if (!user) { navigate('/login?next=/pricing'); return; }
    // plan is the SKU actually sent to checkout; cycle says which toggle
    // state they clicked from (annual is only reachable via the toggle).
    funnelEvent('rl_upgrade_click', { source: 'pricing_page', plan, cycle }, 'studio');
    setUpgradeErr(null);
    setNeedsVerify(null);
    setPending(plan);
    try {
      const { url } = await billing.checkoutSession(plan);
      // Same event /checkout fires, with funnel=pricing so the splitter's
      // own funnel keeps ignoring it (it filters on funnel=splitter).
      funnelEvent('rl_checkout_start', { plan, funnel: 'pricing' }, 'checkout');
      window.location.href = url;
    } catch (e) {
      setPending(null);
      const errCode = (e as { code?: string })?.code || 'unknown';
      funnelEvent('rl_checkout_error', { plan, funnel: 'pricing', code: errCode }, 'checkout');
      // Session expired mid-flow → send them to sign in again; otherwise surface
      // the failure instead of silently reverting the button.
      if ((e as { status?: number })?.status === 401) { navigate('/login?next=/pricing'); return; }
      const api = e as { code?: string; detail?: string };
      if (api?.code === 'email_unverified') {
        setNeedsVerify({ detail: api.detail });
        return;
      }
      setUpgradeErr('Could not start checkout — please try again.');
    }
  }

  // Per-plan CTA label + action, reflecting auth/plan/toggle state.
  function ctaFor(plan: Plan): { label: string; onCta?: () => void; disabled?: boolean } {
    if (plan.id === 'free') {
      if (!user) return { label: 'Sign up free', onCta: () => navigate('/login') };
      if (isPro) return { label: 'Included', disabled: true };
      return { label: "You're on Free", disabled: true };
    }
    // Paid plans (pro monthly/annual + lifetime).
    if (isPro) return { label: "You're on Pro", disabled: true };
    const target = plan.checkout!;
    const loading = pending === target;
    if (!user) {
      const label = plan.id === 'lifetime' ? 'Sign in to get Lifetime' : 'Sign in to get Pro';
      return { label, onCta: () => navigate('/login?next=/pricing') };
    }
    const idle = plan.id === 'lifetime' ? 'Get Lifetime' : 'Get Riffloop Pro';
    return { label: loading ? 'Loading…' : idle, onCta: () => startUpgrade(target), disabled: !!pending };
  }

  return (
    <div className="rlp">
      <div className="app">
        <section className="hero">
          <h1 className="fade-up" style={{ animationDelay: '.05s' }}>
            Simple pricing.<br />
            <span className="grad-text">Serious practice.</span>
          </h1>
          <p className="sub fade-up" style={{ animationDelay: '.12s' }}>
            Start free — no card, no catch. Go Pro when you outgrow the daily limit: monthly, yearly, or once for life.
          </p>
          <Equalizer bars={40} className="hero-eq fade-up" />
        </section>

        <div className="billing fade-up" style={{ animationDelay: '.16s' }}>
          <div className="seg" data-active={cycle}>
            <span className="seg-thumb" />
            <button className={cycle === 'monthly' ? 'on' : ''} onClick={() => setCycle('monthly')}>Monthly</button>
            <button className={cycle === 'annual' ? 'on' : ''} onClick={() => setCycle('annual')}>Yearly</button>
          </div>
          <span className="seg-save">Save 45% yearly</span>
        </div>

        {needsVerify && (
          <div style={{ display: 'flex', justifyContent: 'center', margin: '18px auto 0' }}>
            <VerifyEmailNotice detail={needsVerify.detail} />
          </div>
        )}
        {upgradeErr && (
          <div role="alert" style={{ maxWidth: 560, margin: '18px auto 0', textAlign: 'center', color: '#ff6b6b', fontSize: 14, fontWeight: 600 }}>
            {upgradeErr}
          </div>
        )}
        <div className="cards">
          {plans.map((p) => {
            const c = ctaFor(p);
            return <PlanCard key={p.id} plan={p} ctaLabel={c.label} onCta={c.onCta} ctaDisabled={c.disabled} />;
          })}
        </div>

        <div className="reassure">
          <span><Shield /> No card needed for Free</span>
          <span><Bolt /> Separating songs in seconds</span>
          <span><CardIcon /> Cancel anytime — or pay once for life</span>
        </div>

        <Compare />
        <Faq />

        <section className="final">
          <h2 className="grad-text" style={{ WebkitTextFillColor: '#fff' }}>Ready to hear every part?</h2>
          <p>Head back to the Studio and pull your next track into its stems.</p>
          <button className="btn btn-primary" style={{ display: 'inline-flex' }} onClick={() => navigate('/')}>
            Open the Studio
          </button>
          <Equalizer bars={48} />
        </section>

        <div className="page-bottom" />
      </div>
    </div>
  );
}
